import { useApolloClient } from "@apollo/client";
import { CardContainer, LabeledValue, ModalOpenButton } from "@msys/ui";
import { Edit as EditIcon } from "@mui/icons-material";
import { Box, Divider, IconButton, Stack } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { namedOperations } from "../../../../clients/graphqlTypes.js";
import { useCustomFieldConfigQuery } from "../../custom-fields/customFieldConfigs.generated.js";
import { EditCustomFieldConfigModal } from "../../custom-fields/EditCustomFieldConfigModal.js";
import { useCustomFieldDataTypes } from "../../custom-fields/useCustomFieldDataTypes.js";

export const OrganisationCustomFieldConfigBox: React.FC = () => {
  const { t } = useTranslate(["OrganisationSettings", "CustomFields"]);
  const { customFieldDataTypeLabels } = useCustomFieldDataTypes();

  const client = useApolloClient();
  const customFieldConfigQuery = useCustomFieldConfigQuery({ client });

  // System-managed definitions registered by the PDS integration (group
  // "PDS", pds:* identifier fields) are not user-editable — editing or
  // deleting them would break the sync's identifier writes, so they are
  // hidden here
  const customFieldConfig =
    customFieldConfigQuery.data?.customFieldConfig?.filter(
      field => field.group !== "PDS"
    );
  if (!customFieldConfig) return null;

  return (
    <CardContainer
      title={t("Custom fields configurator", {
        ns: "CustomFields",
      })}
      isInitiallyClosed={false}
      itemCount={customFieldConfig.length}
      ActionButton={
        <ModalOpenButton
          Modal={EditCustomFieldConfigModal}
          modalProps={{
            refetchQueries: [namedOperations.Query.CustomFieldConfig],
          }}
        >
          <IconButton
            color="secondary"
            size="small"
            data-testid="custom-field-config-edit-button"
          >
            <EditIcon />
          </IconButton>
        </ModalOpenButton>
      }
    >
      {customFieldConfig.map((field, index) => (
        <React.Fragment key={index}>
          {index > 0 && <Divider />}
          <Stack
            spacing={1}
            direction="column"
            sx={{
              paddingX: 2,
              paddingY: 1,
            }}
          >
            <Stack spacing={1} direction="row">
              <Box
                sx={{
                  flex: "1",
                }}
              >
                <LabeledValue
                  label={t("Page", {
                    ns: "CustomFields",
                  })}
                >
                  {field.objectType}
                </LabeledValue>
              </Box>
              <Box
                sx={{
                  flex: "1",
                }}
              >
                <LabeledValue
                  label={t("Category", {
                    ns: "CustomFields",
                  })}
                >
                  {field.group}
                </LabeledValue>
              </Box>
            </Stack>
            <Stack spacing={1} direction="row">
              <Box
                sx={{
                  flex: "1",
                }}
              >
                <LabeledValue
                  label={t("Field type", {
                    ns: "CustomFields",
                  })}
                >
                  {customFieldDataTypeLabels[field.dataType]}
                </LabeledValue>
              </Box>
              <Box
                sx={{
                  flex: "1",
                }}
              >
                <LabeledValue
                  label={t("Field label", {
                    ns: "CustomFields",
                  })}
                >
                  {field.label}
                </LabeledValue>
              </Box>
            </Stack>
          </Stack>
        </React.Fragment>
      ))}
    </CardContainer>
  );
};
